import { Check } from "lucide-react";

export type DescriptionSection = { heading: string | null; paragraphs: string[]; bullets: string[] };

/**
 * Beschreibung aus dem Admin („Ausführliche Beschreibung“): Absätze durch
 * Leerzeile getrennt, Zeilen „Ausgangslage“/„Aufgabe“/„Umsetzung“/„Ergebnis“
 * als Zwischentitel, Zeilen mit „•“ als Liste. Gemeinsamer Parser für die
 * Portfolio-Karten und die Fallstudien-Seite – nur belegte Aussagen, nichts
 * wird ergänzt.
 */
export function parseDescription(text: string): DescriptionSection[] {
  return text
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((block) => {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      const [first, ...rest] = lines;
      const isHeading = /^(Ausgangslage|Aufgabe|Umsetzung|Ergebnis)$/i.test(first ?? "");
      const body = isHeading ? rest : lines;
      return {
        heading: isHeading ? first : null,
        bullets: body.filter((l) => l.startsWith("•")).map((l) => l.replace(/^•\s*/, "")),
        paragraphs: body.filter((l) => !l.startsWith("•")),
      };
    });
}

export function DescriptionBlocks({ text, size = "sm" }: { text: string; size?: "sm" | "base" }) {
  const sections = parseDescription(text);
  const textClass = size === "base" ? "text-base" : "text-sm";
  return (
    <div className={size === "base" ? "space-y-6" : "space-y-3"}>
      {sections.map((s, i) => (
        <div key={i}>
          {s.heading && (
            size === "base" ? (
              <h3 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{s.heading}</h3>
            ) : (
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-tertiary)" }}>{s.heading}</p>
            )
          )}
          {s.paragraphs.map((p, j) => (
            <p key={j} className={`mt-1 ${textClass} leading-relaxed`} style={{ color: "var(--text-tertiary)" }}>
              {p}
            </p>
          ))}
          {s.bullets.length > 0 && (
            <ul className={size === "base" ? "mt-3 space-y-2" : "mt-1.5 space-y-1"}>
              {s.bullets.map((b, j) => (
                <li key={j} className={`flex items-start gap-2 ${textClass}`} style={{ color: "var(--text-secondary)" }}>
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" aria-hidden="true" />
                  {b}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
